import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Store, Sparkles, ArrowRight, Gift, CheckCircle2, Shield } from 'lucide-react';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import NoFeeLogo from '../components/NoFeeLogo';
import { LanguageProvider } from '../context/LanguageContext';

/* ─────────────────────────────────────────────────────────────────────
 * Restaurant Referral Landing Page (iter105 — branding alignment).
 *
 * Lives at `/ref/restaurant/:code`. Owner spec (Jun 2026):
 * • Use the official NoFee branding everywhere (Navbar with logo,
 * Footer, official PNG logo at the top).
 * • Replace the bespoke "You were invited to join NoFee" headline
 * with the platform-wide hero copy "0% Food App Fees. Keep More
 * Profit. Get Paid Directly." so that the referral funnel mirrors
 * the live homepage and reinforces brand recall.
 * • Keep the personalized "invitation accepted" pill + referral
 * attribution so the social-proof / urgency angle remains.
 *
 * Flow:
 * 1. Owner lands here from a referral link.
 * 2. We persist `nofee_referral_code` + `nofee_referral_type` in
 * localStorage so the eventual subscription can attribute the
 * +60-point Restaurant Referral reward back to the inviter.
 * 3. Owner clicks "Get Started" → /register?next=onboarding&ref=CODE
 *
 * Pricing is intentionally NOT shown here per the original Feb 2026
 * spec: "Restaurants must understand the platform value before seeing
 * the subscription price."
 * ─────────────────────────────────────────────────────────────────── */

const RestaurantReferralLanding = () => {
 const { code } = useParams();
 const navigate = useNavigate();
 const persistedCode = (code || '').toUpperCase().trim();

 useEffect(() => {
 if (!persistedCode) {
 navigate('/', { replace: true });
 return;
 }
 try {
 localStorage.setItem('nofee_referral_code', persistedCode);
 localStorage.setItem('nofee_referral_type', 'restaurant');
 localStorage.setItem('nofee_referral_at', String(Date.now()));
 } catch {
 /* private-mode / quota — attribution silently degrades */
 }
 }, [persistedCode, navigate]);

 const goNext = () => {
 navigate(`/register?next=onboarding&ref=${persistedCode}`);
 };

 return (
 <LanguageProvider>
 <div
 data-testid="restaurant-referral-landing"
 className="min-h-screen bg-[#0A0A0A] text-white antialiased"
 >
 <SEO
 title="You're invited to join NoFee — 0% Commission Restaurant Platform"
 description="Personal invitation to join NoFee. 0% commission, direct payouts via Stripe. Take orders, keep every dollar — flat monthly subscription only."
 path={`/ref/restaurant/${persistedCode}`}
 noindex
 />
 <Navbar />

 <main className="pt-28 lg:pt-32 pb-20">
 <div className="max-w-3xl mx-auto px-4 sm:px-6">
 {/* Official NoFee logo above the headline */}
 <div className="flex items-center gap-3">
 <NoFeeLogo height={56} />
 <div className="leading-tight">
 <p className="text-[10.5px] uppercase tracking-[0.22em] text-gold-400 font-bold">
 NoFee
 </p>
 <p className="text-[12.5px] text-white/55">0% Commission Restaurant Platform</p>
 </div>
 </div>

 {/* Personal invitation pill */}
 <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08]">
 <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
 <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
 Personal invitation
 </span>
 </div>

 {/* Official platform hero copy */}
 <h1
 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05]"
 data-testid="restaurant-referral-headline"
 >
 <span className="text-gold-400">0%</span> Food App Fees.<br />
 Keep More Profit.<br />
 Get Paid <span className="text-gold-400">Directly</span>.
 </h1>

 <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-xl">
 NoFee helps restaurants keep more profit, receive direct payouts through Stripe, and grow customer relationships without the high fees charged by traditional food apps.
 </p>

 {/* Value bullets */}
 <ul className="mt-8 space-y-3" data-testid="referral-landing-benefits">
 {[
 { Icon: Store, text: 'Beautiful menu page + ordering checkout for your customers' },
 { Icon: CheckCircle2, text: 'Accept card, Apple Pay & Google Pay — direct to your bank via Stripe' },
 { Icon: Shield, text: '45-day free trial · cancel anytime, no commission ever' },
 ].map(({ Icon, text }) => (
 <li key={text} className="flex items-start gap-3 text-[15px] text-white/85">
 <span className="mt-0.5 inline-flex w-7 h-7 rounded-full bg-yellow-500/10 border border-yellow-500/30 items-center justify-center shrink-0">
 <Icon className="w-3.5 h-3.5 text-yellow-300" />
 </span>
 {text}
 </li>
 ))}
 </ul>

 {/* CTA */}
 <button
 type="button"
 onClick={goNext}
 data-testid="referral-landing-cta"
 className="mt-10 inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[15px] hover:brightness-105 transition-all shadow-lg shadow-yellow-500/20"
 >
 Get Started <ArrowRight className="w-4 h-4" />
 </button>

 {/* Referral attribution footer */}
 {persistedCode && (
 <p className="mt-6 text-[12px] text-white/45 flex items-center gap-1.5">
 <Gift className="w-3.5 h-3.5 text-yellow-300/70" /> Invitation code{' '}
 <span className="font-mono text-yellow-300/85">{persistedCode}</span> applied
 </p>
 )}
 </div>
 </main>

 <FooterSection />
 </div>
 </LanguageProvider>
 );
};

export default RestaurantReferralLanding;
